import React from 'react';
import { Icon } from '../icon/Icon.jsx';

/* The workhorse. Real <table> with <th scope>. Numbers right, text left, pills
   and states left, actions right. Units in the header, never in the cell.
   No zebra striping, no pagination, no row shadows. Live rows pin above the sort
   and sorting never reorders the live block. */

const DENSITY = {
  comfortable: { row: 40, pad: 12, num: 'var(--num-sm-size)', grid: false },
  compact: { row: 32, pad: 8, num: 'var(--num-xs-size)', grid: false },
  dense: { row: 26, pad: 6, num: 'var(--num-xs-size)', grid: true },
};

export function DataTable({
  columns = [], rows = [], density = 'comfortable', sortKey, sortDir = 'desc', onSort,
  selectedId, onSelectRow, expandedId, renderExpanded, rowKey = 'id', stickyFirst = false,
  empty = 'Nothing here yet.', style,
}) {
  const d = DENSITY[density] || DENSITY.comfortable;
  const live = rows.filter((r) => r.mode === 'live');
  const rest = rows.filter((r) => r.mode !== 'live');
  const ordered = live.concat(rest);

  const modeBorder = (r) => r.mode === 'live'
    ? '3px solid var(--live)'
    : r.mode === 'paper' ? '3px dashed var(--paper)' : '3px solid transparent';

  const cellBase = {
    padding: '0 ' + d.pad + 'px', height: d.row,
    borderBottom: '1px solid var(--border-subtle)',
    fontSize: d.num, lineHeight: 1, whiteSpace: 'nowrap',
  };

  return (
    <div style={{ overflowX: 'auto', ...style }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-ui)' }}>
        <thead>
          <tr>
            {columns.map((c, ci) => (
              <th key={c.key} scope="col"
                aria-sort={sortKey === c.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                onClick={() => c.sortable && onSort && onSort(c.key)}
                className={c.sortable ? 'jmt-th jmt-th--sortable' : 'jmt-th'}
                style={{
                  position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg-inset)',
                  textAlign: c.align === 'right' ? 'right' : 'left',
                  padding: '0 ' + d.pad + 'px', height: d.row,
                  borderBottom: '1px solid var(--border-default)',
                  borderRight: d.grid && ci < columns.length - 1 ? '1px solid var(--border-grid)' : 'none',
                  color: 'var(--text-tertiary)', fontSize: 'var(--label-size)', fontWeight: 'var(--label-weight)',
                  letterSpacing: 'var(--label-tracking)', textTransform: 'uppercase', whiteSpace: 'nowrap',
                  cursor: c.sortable ? 'pointer' : 'default', userSelect: 'none',
                  left: stickyFirst && ci === 0 ? 0 : undefined,
                }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start', width: '100%' }}>
                  {c.label}
                  {c.sortable ? (
                    <span style={{ opacity: sortKey === c.key ? 1 : 0 }} className="jmt-sortglyph">
                      <Icon name={sortDir === 'asc' ? 'arrow-up' : 'arrow-down'} size={11} />
                    </span>
                  ) : null}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ordered.length === 0 ? (
            <tr><td colSpan={columns.length} style={{ ...cellBase, height: 'auto', padding: 'var(--space-24)', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 'var(--body-size)', whiteSpace: 'normal' }}>{empty}</td></tr>
          ) : ordered.map((r, ri) => {
            const id = r[rowKey];
            const sel = selectedId != null && selectedId === id;
            const pinBoundary = live.length > 0 && ri === live.length - 1;
            const dim = r.participating === false;
            return (
              <React.Fragment key={id != null ? id : ri}>
                <tr onClick={() => onSelectRow && onSelectRow(r)} className="jmt-tr"
                  data-selected={sel ? '' : undefined}
                  style={{
                    background: sel ? 'var(--accent-subtle)' : 'transparent',
                    cursor: onSelectRow ? 'pointer' : 'default',
                  }}>
                  {columns.map((c, ci) => (
                    <td key={c.key}
                      style={{
                        ...cellBase,
                        textAlign: c.align === 'right' ? 'right' : 'left',
                        borderBottom: pinBoundary ? '1px solid var(--border-default)' : cellBase.borderBottom,
                        borderRight: d.grid && ci < columns.length - 1 ? '1px solid var(--border-grid)' : 'none',
                        borderLeft: ci === 0 ? (sel ? '2px solid var(--accent)' : modeBorder(r)) : undefined,
                        color: dim ? 'var(--text-secondary)' : 'var(--text-primary)',
                        fontVariantNumeric: c.align === 'right' ? 'tabular-nums slashed-zero' : 'normal',
                        fontFamily: c.mono ? 'var(--font-mono)' : 'inherit',
                        position: stickyFirst && ci === 0 ? 'sticky' : undefined,
                        left: stickyFirst && ci === 0 ? 0 : undefined,
                        background: stickyFirst && ci === 0 ? 'var(--bg-surface)' : undefined,
                      }}>
                      {c.render ? c.render(r) : (r[c.key] == null || r[c.key] === '' ? <span style={{ color: 'var(--text-tertiary)' }}>—</span> : r[c.key])}
                    </td>
                  ))}
                </tr>
                {expandedId != null && expandedId === id && renderExpanded ? (
                  <tr>
                    <td colSpan={columns.length} style={{ padding: 0, background: 'var(--bg-inset)', borderBottom: '1px solid var(--border-subtle)', boxShadow: 'var(--edge-inset)' }}>
                      <div className="jmt-expand" data-open="">
                        <div>
                          <div style={{ borderLeft: '2px solid var(--border-default)', margin: '0 0 0 ' + d.pad + 'px' }}>{renderExpanded(r)}</div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      <style>{`
        /* Colour only. A table row must not lift, scale or shift under the pointer:
           in a virtualised list of two hundred rows any geometry change on hover is a
           moving target the operator is trying to click, and the row directly under
           the cursor is the one they are reading a number off. */
        .jmt-tr{transition:background var(--dur-state) var(--ease-out)}
        .jmt-tr:hover{background:var(--bg-raised)!important}
        /* A selected row still has somewhere to go on hover. Before --accent-faint
           existed, hovering the selected row did nothing and the row read as dead. */
        .jmt-tr[data-selected]:hover{
          background:color-mix(in srgb, var(--accent-subtle) 100%, var(--accent-faint))!important
        }
        .jmt-sortglyph{transition:opacity var(--dur-state) var(--ease-out)}
        .jmt-th--sortable:hover .jmt-sortglyph{opacity:.6}
        .jmt-th--sortable{transition:color var(--dur-state) var(--ease-out)}
        .jmt-th--sortable:hover{color:var(--text-secondary)}
        /* The disclosure body. grid-template-rows 0fr to 1fr is the only way to
           transition to an intrinsic height without measuring it in JS; the child
           needs min-height 0 and overflow hidden for the clip to work. */
        .jmt-expand{
          display:grid;grid-template-rows:0fr;
          transition:grid-template-rows var(--dur-row-expand) var(--ease-out);
        }
        .jmt-expand[data-open]{grid-template-rows:1fr}
        .jmt-expand > *{min-height:0;overflow:hidden}
      `}</style>
    </div>
  );
}
